/**
 * The fonts an imported document uses, and how each is drawn.
 *
 * A font comes in from the PDF in one of three states, and the person can
 * improve any of them:
 *
 * - **complete**: a full font is at hand, because it is freely available
 *   (Montserrat) or because somebody uploaded the file. Every character works.
 * - **partial**: the PDF carried a subset. What was printed reproduces
 *   exactly; a character it lacks is drawn in the substitute, never left blank.
 * - **missing**: nothing usable came with the PDF. The substitute draws it all.
 *
 * Every font is drawn through a stack, best first: the uploaded file, the
 * subset from the PDF, the open family, the substitute. The browser takes each
 * character from the first that has it, which is exactly the fallback the
 * person is promised.
 *
 * Pure. Names, stacks and CSS text; loading is somebody else's job.
 */

export interface ImportedFont {
  /** The PDF's font name without its subset prefix, which every run names. */
  readonly key: string
  readonly family: string
  readonly weight: number
  readonly italic: boolean
  /** The subset the PDF carried, as a data URL; empty when there was none. */
  readonly embedded: string
  /** Every character `embedded` draws. */
  readonly characters: string
  /** The same family, freely available in full, when there is one. */
  readonly openFamily: string
  /** What draws the characters nothing better has. */
  readonly substitute: string
  /** A full font somebody supplied, as a data URL; empty until they do. */
  readonly uploaded: string
}

export type FontStatus = 'complete' | 'partial' | 'missing'

/** The free families offered as a replacement, all served by Google Fonts for the demo. */
export const REPLACEMENT_FAMILIES = [
  'Source Sans 3',
  'Inter',
  'Montserrat',
  'Open Sans',
  'Roboto',
  'Lato',
  'Arimo',
  'Noto Sans',
  'IBM Plex Sans',
  'Tinos',
  'Source Serif 4',
  'Merriweather',
  'EB Garamond',
  'Lora',
] as const

const GENERIC = 'sans-serif'

export function fontStatus(font: ImportedFont): FontStatus {
  if (font.uploaded || font.openFamily) return 'complete'
  return font.embedded ? 'partial' : 'missing'
}

/** Whether the font draws what was printed with its own glyphs, as opposed to a stand-in. */
export function isExact(font: ImportedFont | undefined): boolean {
  return Boolean(font && (font.uploaded || font.embedded || font.openFamily))
}

function cssName(prefix: string, key: string): string {
  return `${prefix}-${key.replace(/[^A-Za-z0-9-]/g, '')}`
}

export function embeddedFamily(key: string): string {
  return cssName('pdf', key)
}

export function uploadedFamily(key: string): string {
  return cssName('pdfu', key)
}

/** The CSS font-family stack a run in this font is drawn with. */
export function fontStack(font: ImportedFont | undefined): string {
  if (!font) return GENERIC
  const families = [
    font.uploaded ? uploadedFamily(font.key) : '',
    font.embedded ? embeddedFamily(font.key) : '',
    font.openFamily,
    font.substitute,
  ].filter(Boolean)
  return [...families.map((family) => `'${family}'`), GENERIC].join(', ')
}

/** The weight and slant to ask the open family and the substitute for. */
export function fontVariant(font: ImportedFont | undefined): { weight: number; style: string } {
  return { weight: font?.weight ?? 400, style: font?.italic ? 'italic' : 'normal' }
}

/**
 * The characters of a text this font cannot draw with its own glyphs.
 *
 * Empty for a complete font. For a partial one, whatever is not in the subset;
 * spaces never count, since every font has them.
 */
export function missingCharacters(text: string, font: ImportedFont | undefined): string {
  if (!font || fontStatus(font) === 'complete') return ''
  const missing = new Set<string>()
  for (const character of text) {
    if (character.trim() !== '' && !font.characters.includes(character)) missing.add(character)
  }
  return [...missing].join('')
}

/**
 * The @font-face rules for every font file the document holds.
 *
 * Only the files themselves: the open families and substitutes are loaded by
 * name (see `features/typography/importedFonts.ts`). `font-display: block`
 * so a page never flashes through a stand-in before its own font arrives.
 *
 * Each file is one style already (the PDF's bold is its own font), yet a run
 * still asks for its weight and slant so the fallbacks behind it match. The
 * face claims every weight and slant so the browser draws its glyphs as they
 * are, rather than thickening a bold or slanting an italic a second time.
 */
export function fontFaceRules(fonts: readonly ImportedFont[]): string {
  return fonts
    .flatMap((font) => [
      font.embedded ? [embeddedFamily(font.key), font.embedded] : null,
      font.uploaded ? [uploadedFamily(font.key), font.uploaded] : null,
    ])
    .filter((entry): entry is string[] => entry !== null)
    .map(
      ([family, source]) =>
        `@font-face{font-family:'${family}';src:url(${source});font-display:block;` +
        `font-weight:1 1000;font-style:oblique -90deg 90deg}`,
    )
    .join('\n')
}

/** Every family that is loaded by name rather than from a file the document holds. */
export function namedFamilies(fonts: readonly ImportedFont[]): string[] {
  return [
    ...new Set(fonts.flatMap((font) => [font.openFamily, font.substitute]).filter(Boolean)),
  ].sort()
}

/**
 * The fonts a document holds, with those of a new import taken in.
 *
 * A font already held keeps what somebody decided about it, its uploaded file
 * and its substitute. Of the two subsets, the one that draws more characters
 * is kept: two subsets cannot be joined here, and the larger one covers more
 * of whatever is typed next.
 */
export function mergeImportedFonts(
  held: readonly ImportedFont[],
  incoming: readonly ImportedFont[],
): ImportedFont[] {
  const byKey = new Map(held.map((font) => [font.key, font]))
  for (const font of incoming) {
    const kept = byKey.get(font.key)
    if (!kept) {
      byKey.set(font.key, font)
      continue
    }
    const larger = font.characters.length > kept.characters.length ? font : kept
    byKey.set(font.key, { ...kept, embedded: larger.embedded, characters: larger.characters })
  }
  return [...byKey.values()]
}
